const SkeletonCard = ({ children, className = "" }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border border-gray-100 bg-white dark:border-stone-700 dark:bg-stone-800 ${className}`}
  >
    <div className="pointer-events-none absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-gray-100/70 to-transparent dark:via-stone-700/40" />
    <div className="relative">{children}</div>
  </div>
);

const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded bg-gray-200/80 dark:bg-stone-700/70 ${className}`} />
);

export default function DashboardSkeleton({ showConfirmators = false }) {
  return (
    <div className="space-y-8" role="status" aria-label="Loading dashboard">
      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} className="p-6">
            <div className="flex items-start gap-4">
              <SkeletonBlock className="h-11 w-11 flex-shrink-0 rounded-2xl" />
              <div className="flex-1 space-y-3">
                <SkeletonBlock className="h-3 w-24" />
                <SkeletonBlock className="h-7 w-32 rounded-lg" />
                <SkeletonBlock className="h-3 w-20" />
              </div>
            </div>
            <SkeletonBlock className="mt-5 h-1 w-full rounded-full bg-gray-100/80 dark:bg-stone-700/60" />
          </SkeletonCard>
        ))}
      </div>

      {/* Order status cards */}
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonCard key={i} className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <SkeletonBlock className="h-5 w-14 rounded-full" />
                <SkeletonBlock className="h-8 w-12 rounded-md" />
                <SkeletonBlock className="h-3 w-20" />
              </div>
              <SkeletonBlock className="h-10 w-10 rounded-xl" />
            </div>
            <div className="mt-5 space-y-2">
              <SkeletonBlock className="h-3 w-16" />
              <SkeletonBlock className="h-1.5 w-full rounded-full bg-gray-100/80 dark:bg-stone-700/60" />
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <div className="xl:col-span-2 space-y-6">
          <div className="w-full grid grid-cols-2 max-md:grid-cols-1 items-center gap-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <SkeletonCard key={i}>
                <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
                  <SkeletonBlock className="h-4 w-36" />
                  <SkeletonBlock className="h-3 w-24" />
                </div>
                <div className="px-6 py-4 flex items-center justify-center">
                  <SkeletonBlock className="h-32 w-32 rounded-full" />
                </div>
              </SkeletonCard>
            ))}

            {/* Meilleurs confirmateurs */}
            {showConfirmators && (
              <SkeletonCard>
                <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
                  <SkeletonBlock className="h-4 w-40" />
                  <SkeletonBlock className="h-3 w-28" />
                </div>
                <div className="max-w-max max-h-max pb-4 pt-2 px-4 space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <SkeletonBlock className="h-8 w-8 rounded-lg" />
                      <SkeletonBlock className="h-3 w-24" />
                      <SkeletonBlock className="h-5 w-16 rounded-full" />
                    </div>
                  ))}
                </div>
              </SkeletonCard>
            )}

            {/* Temps de livraison */}
            <SkeletonCard className={`${showConfirmators ? "" : "col-span-2"}`}>
              <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
                <SkeletonBlock className="h-4 w-40" />
                <SkeletonBlock className="h-3 w-32" />
              </div>
              <div className="px-6 py-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <SkeletonBlock className="h-3 w-20" />
                    <SkeletonBlock className="h-5 flex-1 rounded-full bg-gray-100/80 dark:bg-stone-700/50" />
                    <SkeletonBlock className="h-3 w-10" />
                  </div>
                ))}
              </div>
            </SkeletonCard>
          </div>

          {Array.from({ length: 2 }).map((_, i) => (
            <SkeletonCard key={i} className="p-4">
              <div className="space-y-4">
                <SkeletonBlock className="h-4 w-40" />
                <SkeletonBlock className="h-52 w-full rounded-lg bg-gray-100/80 dark:bg-stone-700/50" />
              </div>
            </SkeletonCard>
          ))}
        </div>

        <div className="grid gap-x-6 space-y-6 xl:col-span-1 md:grid-cols-2 xs:grid-cols-1">
          <SkeletonCard className="xl:col-span-2">
            <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-3 w-24" />
            </div>
            <div className="p-4">
              <SkeletonBlock className="h-40 w-full rounded-lg bg-gray-100/80 dark:bg-stone-700/50" />
            </div>
          </SkeletonCard>

          <SkeletonCard className="xl:col-span-2">
            <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-3 w-24" />
            </div>
            <div className="space-y-4 p-4">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-8 w-8 rounded-lg" />
                    <div className="space-y-2">
                      <SkeletonBlock className="h-3 w-24" />
                      <SkeletonBlock className="h-3 w-16" />
                    </div>
                  </div>
                  <SkeletonBlock className="h-3 w-14" />
                </div>
              ))}
            </div>
          </SkeletonCard>

          <SkeletonCard className="xl:col-span-2">
            <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-3 w-24" />
            </div>
            <div className="space-y-4 p-4">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-8 w-8 rounded-lg" />
                    <div className="space-y-2">
                      <SkeletonBlock className="h-3 w-24" />
                      <SkeletonBlock className="h-3 w-16" />
                    </div>
                  </div>
                  <SkeletonBlock className="h-3 w-14" />
                </div>
              ))}
            </div>
          </SkeletonCard>

          {/* Top 5 des villes */}
          <SkeletonCard className="xl:col-span-2">
            <div className="space-y-2 border-b border-gray-100 p-4 dark:border-stone-700">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-3 w-24" />
            </div>
            <div className="px-4 pt-2">
              <div className="flex items-center justify-between border-b border-gray-100 dark:border-stone-700 py-2">
                <SkeletonBlock className="h-3 w-16" />
                <SkeletonBlock className="h-3 w-12" />
                <SkeletonBlock className="h-3 w-16" />
                <SkeletonBlock className="h-3 w-10" />
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-stone-700/50">
                  <div className="flex items-center gap-2">
                    <SkeletonBlock className="h-3.5 w-3.5 rounded" />
                    <SkeletonBlock className="h-3 w-20" />
                  </div>
                  <SkeletonBlock className="h-3 w-8" />
                  <SkeletonBlock className="h-3 w-8" />
                  <SkeletonBlock className="h-4 w-10 rounded-full" />
                </div>
              ))}
            </div>
          </SkeletonCard>
        </div>
      </div>
    </div>
  );
}
